/**
 * useMutationSaga — DRYs the submit + poll lifecycle of the F&O/Sage mutation
 * saga so the org drawer panels don't each re-implement it.
 *
 * Flow: submit() → phase 'submitting' → (202) → 'polling' (getMutation every
 * 1500ms until overall_status is terminal) → 'completed' | 'partial' | 'failed'.
 * The poll timer is cleared on unmount and on every fresh submit/reset.
 *
 * Callers get everything they need to drive the two soft-edges:
 *   - `foErrorStage` — the saga's `fo_error.stage` on a terminal failure, so a
 *     caller can detect `preflight_no_position_defaults` and offer a re-submit.
 *   - `errorStatus`  — the HTTP status of a *thrown* submit (e.g. 403 when the
 *     caller has no mutation role), so the UI can fall back to HR-Ops.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  mutationsService,
  isTerminal,
  type MutationResponse,
  type CreateMutationRequest,
} from '@/services/api/mutationsService';

export type MutationSagaPhase =
  | 'idle'
  | 'submitting'
  | 'polling'
  | 'completed'
  | 'partial'
  | 'failed';

export interface UseMutationSaga {
  /** Current lifecycle phase. */
  phase: MutationSagaPhase;
  /** Latest saga record (populated once the 202 lands, then on each poll). */
  mutation: MutationResponse | null;
  /** Human-readable error for a terminal failure or a thrown submit. */
  error: string | null;
  /** HTTP status of a *thrown* submit (403 → no mutation role). */
  errorStatus: number | null;
  /** `fo_error.stage` of a terminal failure — drives the soft-gate. */
  foErrorStage: string | null;
  /** true while submitting or polling (lock the form). */
  isBusy: boolean;
  submit: (employeeCode: string, request: CreateMutationRequest) => void;
  reset: () => void;
}

const POLL_MS = 1500;
const POLL_RETRY_MS = 2500;
// Stop retrying a persistently-failing status poll after ~60s (24 × 2500ms) so
// a sustained integration/gateway outage surfaces an actionable outcome instead
// of a perpetual spinner + locked form. The 202 already landed, so the change
// may well have committed — say so and unlock.
const MAX_POLL_FAILURES = 24;

function failureMessage(m: MutationResponse): string {
  const stage = m.fo_error?.stage ?? m.sage_error?.stage;
  const detail = m.fo_error?.error ?? m.sage_error?.error;
  if (detail) {
    return `The change could not be completed${stage ? ` (${stage})` : ''}: ${detail}`;
  }
  return `The change ended in "${m.overall_status}". Review the details below.`;
}

export function useMutationSaga(): UseMutationSaga {
  const [phase, setPhase] = useState<MutationSagaPhase>('idle');
  const [mutation, setMutation] = useState<MutationResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [errorStatus, setErrorStatus] = useState<number | null>(null);
  const [foErrorStage, setFoErrorStage] = useState<string | null>(null);

  const pollRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const mountedRef = useRef(true);

  const clearPoll = useCallback(() => {
    if (pollRef.current) {
      clearTimeout(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      clearPoll();
    };
  }, [clearPoll]);

  const reset = useCallback(() => {
    clearPoll();
    setPhase('idle');
    setMutation(null);
    setError(null);
    setErrorStatus(null);
    setFoErrorStage(null);
  }, [clearPoll]);

  const submit = useCallback(
    (employeeCode: string, request: CreateMutationRequest) => {
      clearPoll();
      setPhase('submitting');
      setMutation(null);
      setError(null);
      setErrorStatus(null);
      setFoErrorStage(null);

      mutationsService
        .submitMutation(employeeCode, request)
        .then((resp) => {
          if (!mountedRef.current) return;
          setMutation(resp);
          setPhase('polling');

          let pollFailures = 0;
          const poll = async () => {
            try {
              const latest = await mutationsService.getMutation(
                employeeCode,
                resp.mutation_id,
              );
              if (!mountedRef.current) return;
              pollFailures = 0; // a good read clears the transient-failure streak
              setMutation(latest);
              if (!isTerminal(latest.overall_status)) {
                pollRef.current = setTimeout(poll, POLL_MS);
                return;
              }
              if (latest.overall_status === 'completed') {
                setPhase('completed');
              } else if (latest.overall_status === 'partial') {
                setPhase('partial');
              } else {
                setFoErrorStage(latest.fo_error?.stage ?? null);
                setError(failureMessage(latest));
                setPhase('failed');
              }
            } catch {
              if (!mountedRef.current) return;
              pollFailures += 1;
              if (pollFailures >= MAX_POLL_FAILURES) {
                // Persistent failure (outage / circuit-breaker) — stop polling and
                // unlock the form. The submit already succeeded (202), so the
                // change may already be applied; point the operator at HR-Ops.
                setError(
                  'Submitted, but the status could not be confirmed (the service ' +
                    'may be unavailable). The change may already be applied — ' +
                    'verify in HR-Ops before retrying.',
                );
                setPhase('failed');
                return;
              }
              // Transient GET hiccup — retry a little slower, don't fail the saga.
              pollRef.current = setTimeout(poll, POLL_RETRY_MS);
            }
          };
          pollRef.current = setTimeout(poll, 1000);
        })
        .catch((e: unknown) => {
          if (!mountedRef.current) return;
          const err = e as {
            response?: { status?: number; data?: { detail?: string } };
            message?: string;
          };
          setErrorStatus(err?.response?.status ?? null);
          setError(
            err?.response?.data?.detail ??
              err?.message ??
              'Submission failed. Please try again.',
          );
          setPhase('failed');
        });
    },
    [clearPoll],
  );

  return {
    phase,
    mutation,
    error,
    errorStatus,
    foErrorStage,
    isBusy: phase === 'submitting' || phase === 'polling',
    submit,
    reset,
  };
}

export default useMutationSaga;
